import React, { useState } from "react";
import { Layout } from "lucide-react";
import { cn } from "../../lib/utils";
import Card from "../ui/Card";

const LayoutSettings = ({ previewSettings, onSettingsChange }) => {
  const [customPaddingValue, setCustomPaddingValue] = useState(
    previewSettings.customPadding || 32
  );

  const handleChange = (key, value) => {
    onSettingsChange({ [key]: value });
  };

  const aspectRatios = [
    { value: "auto", label: "Auto" },
    { value: "1:1", label: "1:1" },
    { value: "3:4", label: "3:4" },
    { value: "4:5", label: "4:5" },
    { value: "9:16", label: "9:16" },
    { value: "16:9", label: "16:9" },
  ];

  const paddingOptions = [
    { value: "compact", label: "Compact" },
    { value: "standard", label: "Standard" },
    { value: "spacious", label: "Spacious" },
    { value: "custom", label: "Custom" },
  ];

  const handlePaddingTypeChange = (paddingType) => {
    handleChange("paddingType", paddingType);
    if (paddingType !== "custom") {
      // Set default padding values for non-custom options
      const defaultPaddings = {
        compact: 16,
        standard: 32,
        spacious: 64,
      };
      handleChange("customPadding", defaultPaddings[paddingType] || 32);
    }
  };

  const handleCustomPaddingChange = (value) => {
    const numValue = parseInt(value) || 0;
    setCustomPaddingValue(numValue);
    if (previewSettings.paddingType === "custom") {
      handleChange("customPadding", numValue);
    }
  };

  const handleCustomPaddingBlur = () => {
    handleChange("customPadding", customPaddingValue);
  };

  return (
    <Card className="p-6 space-y-6">
      <h3 className="text-lg font-bold text-white mb-4 flex items-center gap-2">
        <Layout className="w-5 h-5" />
        Layout Settings
      </h3>

      {/* Aspect Ratio */}
      <div>
        <label className="text-sm font-medium text-zinc-300 mb-3 block">
          Aspect Ratio
        </label>
        <div className="grid grid-cols-3 gap-2">
          {aspectRatios.map((ratio) => (
            <button
              key={ratio.value}
              onClick={() => handleChange("aspectRatio", ratio.value)}
              className={cn(
                "px-3 py-2 rounded-lg text-sm font-medium transition-all",
                previewSettings.aspectRatio === ratio.value
                  ? "bg-blue-600 text-white"
                  : "bg-zinc-800 text-zinc-300 hover:bg-zinc-700"
              )}
            >
              {ratio.label}
            </button>
          ))}
        </div>
      </div>

      {/* Padding */}
      <div>
        <label className="text-sm font-medium text-zinc-300 mb-3 block">
          Padding
        </label>
        <div className="grid grid-cols-2 gap-2 mb-3">
          {paddingOptions.map((option) => (
            <button
              key={option.value}
              onClick={() => handlePaddingTypeChange(option.value)}
              className={cn(
                "px-3 py-2 rounded-lg text-sm font-medium transition-all",
                previewSettings.paddingType === option.value
                  ? "bg-blue-600 text-white"
                  : "bg-zinc-800 text-zinc-300 hover:bg-zinc-700"
              )}
            >
              {option.label}
            </button>
          ))}
        </div>
        {previewSettings.paddingType === "custom" && (
          <div className="flex items-center gap-2">
            <input
              type="number"
              min="0"
              max="200"
              value={customPaddingValue}
              onChange={(e) => handleCustomPaddingChange(e.target.value)}
              onBlur={handleCustomPaddingBlur}
              className="flex-1 px-3 py-2 rounded-md border border-zinc-700 bg-zinc-900 text-white text-sm"
              placeholder="32"
            />
            <span className="text-sm text-zinc-400">px</span>
          </div>
        )}
      </div>

      {/* Logo Style */}
      <div>
        <label className="text-sm font-medium text-zinc-300 mb-3 block">
          Logo Style
        </label>
        <div className="grid grid-cols-3 gap-2">
          {/* Twitter Logo */}
          <button
            onClick={() => handleChange("logoStyle", "twitter")}
            className={cn(
              "px-3 py-2 rounded-lg text-sm font-medium transition-all flex items-center justify-center",
              previewSettings.logoStyle === "twitter"
                ? "bg-blue-600 text-white"
                : "bg-zinc-800 text-zinc-300 hover:bg-zinc-700"
            )}
            title="Twitter Logo"
          >
            <svg
              viewBox="0 0 24 24"
              className="w-5 h-5 fill-current"
              aria-hidden="true"
            >
              <path d="M23.953 4.57a10 10 0 01-2.825.775 4.958 4.958 0 002.163-2.723c-.951.555-2.005.959-3.127 1.184a4.92 4.92 0 00-8.384 4.482C7.69 8.095 4.067 6.13 1.64 3.162a4.822 4.822 0 00-.666 2.475c0 1.71.87 3.213 2.188 4.096a4.904 4.904 0 01-2.228-.616v.06a4.923 4.923 0 003.946 4.827 4.996 4.996 0 01-2.212.085 4.936 4.936 0 004.604 3.417 9.867 9.867 0 01-6.102 2.105c-.39 0-.779-.023-1.17-.067a13.995 13.995 0 007.557 2.209c9.053 0 13.998-7.496 13.998-13.985 0-.21 0-.42-.015-.63A9.935 9.935 0 0024 4.59z" />
            </svg>
          </button>

          {/* X Logo */}
          <button
            onClick={() => handleChange("logoStyle", "x")}
            className={cn(
              "px-3 py-2 rounded-lg text-sm font-medium transition-all flex items-center justify-center",
              previewSettings.logoStyle === "x"
                ? "bg-blue-600 text-white"
                : "bg-zinc-800 text-zinc-300 hover:bg-zinc-700"
            )}
            title="X Logo"
          >
            <svg
              viewBox="0 0 24 24"
              className="w-5 h-5 fill-current"
              aria-hidden="true"
            >
              <path d="M13.3174 10.7749L19.1457 4H17.7646L12.7039 10.1375L8.66193 4H4L10.1122 12.8958L4 20.7393H5.38119L10.7254 14.3578L14.952 20.7393H19.6139L13.3171 10.7749H13.3174ZM11.4257 13.3364L10.8064 12.4721L5.87886 4.72784H8.00029L12.1717 10.8689L12.7909 11.7332L17.7652 19.9775H15.6438L11.4257 13.3364Z" />
            </svg>
          </button>

          {/* None */}
          <button
            onClick={() => handleChange("logoStyle", "none")}
            className={cn(
              "px-3 py-2 rounded-lg text-sm font-medium transition-all",
              previewSettings.logoStyle === "none"
                ? "bg-blue-600 text-white"
                : "bg-zinc-800 text-zinc-300 hover:bg-zinc-700"
            )}
            title="No Logo"
          >
            None
          </button>
        </div>
      </div>
    </Card>
  );
};

export default LayoutSettings;

